<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    :create-to="`/admin/keys/${keyId}/csr/new`"
    create-label="Crear CSR"
    create-icon="mdi-file-plus"
    remove-title="Eliminar CSR"
  >
    <template #actions>
      <v-btn variant="text" color="indigo" :to="`/admin/keys/${keyId}/edit`">
        <v-icon class="mr-2">mdi-key</v-icon>Ver llave
      </v-btn>
    </template>
  </ag-index-page>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { saveAs } from "file-saver";
import type { Collection, KeyCsr } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { csrStateLabel } from "@/utils/labels";

const route = useRoute();
const keyId = computed(() => Number(route.params.key_id));

/**
 * GET /admin/keys/{id}/csr devuelve un ARRAY plano, no un Page. Se envuelve en
 * la forma de colección para reutilizar el mismo cascarón de índice; la
 * paginación queda del lado del cliente porque un CSR por llave son pocos.
 */
// Sin filtros: se deja el F por omisión (`string`); usar `never` rompe la
// contravarianza del parámetro al pasar el objeto al cascarón.
const view = useIndexView<KeyCsr>({
  title: "CSR de la llave",
  rowKey: "id",
  fetchPage: async () => {
    const list = await banco.admin.keys.listCsr(keyId.value);
    return { content: list, totalElements: list.length } as unknown as Collection<KeyCsr>;
  },
  remove: (item) => banco.admin.keys.destroyCsr(keyId.value, item.id),
  removedMessage: (item) => `Se eliminó el CSR ${item.id}.`,
  confirm: {
    expected: (item) => String(item.id),
    label: "ID del CSR",
    hint: "Para confirmar ingresa el <b>ID</b> del CSR a eliminar.",
    error: "El ID del CSR no es correcto.",
    warning: (item) => `Esta acción eliminará el CSR ${item.id}.`,
  },
});

function downloadCsr(item: KeyCsr) {
  const blob = new Blob([item.csr], { type: "text/plain;charset=utf-8" });
  saveAs(blob, ["bcentral", item.id, Date.now()].join("_") + ".csr");
}

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "ID", value: "id", class: "text-no-wrap" },
    row: { type: "CHIP", label: true, color: {} },
  },
  {
    header: { text: "Estado", value: "state", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { PENDING: "warning", COMPLETED: "success" },
      transform: (val) => csrStateLabel(val),
    },
  },
  {
    header: { text: "Certificado", value: "certificate_fingerprint", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/certificates/{certificate_fingerprint}/edit" },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        {
          label: "Descargar CSR",
          icon: "mdi-download",
          action: (item) => downloadCsr(item as KeyCsr),
        },
        {
          label: "Importar Certificado",
          icon: "mdi-file-import",
          // `{$key_id}` interpola contra los params de la ruta actual.
          link: "/admin/certificates/new?key_id={$key_id}&csr_id={id}",
          // Un CSR ya completado tiene su certificado: importar otro no aplica.
          disabled: (item) => (item as KeyCsr).state === "COMPLETED",
        },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as KeyCsr),
        },
      ],
    },
  },
];
</script>
